<template>
  <chart :option="calOptions"></chart>
</template>

<script>
import dayjs from 'dayjs';
import Chart from '@/components/echarts/chart.vue';
import { optionGen, getLegendKey, thousandsBeautifyNumber } from '@/utils/helper';

export default {
  components: {
    Chart,
  },
  props: {
    options: {
      type: Object,
      required: true,
    },
    chartOptions: {
      type: Object,
      required: false,
    },
    unit: {
      type: String,
      default: 'day',
    },
    dateKeys: {
      type: Object,
      required: true,
    },
    legendData: {
      type: Array,
      required: true,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    showCompareDate: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    calOptions() {
      const { showCompareDate, options, legendData, dateKeys, unit } = this;
      const { seriesMap, seriesMapCompare } = options;
      const { baseDate, compareDate } = dateKeys;

      const filterLegendData = legendData.filter(item => !!item.show);
      const option = optionGen();
      option.color = filterLegendData.map(item => item.color);

      delete option.xAxis;
      delete option.yAxis;

      if (!showCompareDate) {
        // const baseTimeRang = [
        //   dayjs(baseDateKeys.shift()).format(unit === 'day' ? 'YYYY-MM-DD' : 'MM-DD HH:mm'),
        //   dayjs(baseDateKeys.pop()).format(unit === 'day' ? 'YYYY-MM-DD' : 'MM-DD HH:mm'),
        // ];
        const filterSeriesMap = filterLegendData.map(
          item => seriesMap.find(ti => ti.wdUniqKey === item.name) || { valSum: 0 },
        );

        // 没有对比日期的情况
        option.title = {
          subtext: thousandsBeautifyNumber(filterSeriesMap.reduce((base, item) => base + (item.valSum || 0), 0)),
          text: '数据项',
          subtextStyle: {
            color: '#222222',
            fontWeight: 'normal',
            fontStyle: 'normal',
            fontSize: 18,
          },
          textStyle: {
            color: '#666666',
            fontWeight: 'normal',
            fontStyle: 'normal',
            fontSize: 12,
            align: 'center',
          },
          left: this.showLegend ? '34.7%' : '49.8%',
          textAlign: 'center',
          textVerticalAlign: 'middle',
          top: '42%',
        };

        option.series = [
          {
            type: 'pie',
            radius: ['50%', '60%'],
            avoidLabelOverlap: false,
            label: {
              show: true,
              formatter: '{d}%',
              ellipsis: true,
            },
            minAngle: 20,
            left: '0%',
            right: this.showLegend ? '30%' : '0%',
            data: filterSeriesMap.map(item => ({
              name: item.wdUniqKeyDesc,
              value: item.valSum,
            })),
          },
        ];
        option.legend = {
          type: 'scroll',
          orient: 'vertical',
          left: '70%',
          top: '20%',
          icon: 'circle',
          itemHeight: 10,
          itemWidth: 10,
          bottom: '20%',
          tooltip: {
            show: true,
          },
          formatter(name) {
            return name.length > 10 ? `${name.substr(0, 10)}...` : name;
          },
        };
      } else {
        const baseTimeRang = [
          dayjs(baseDate[0]).format(unit === 'day' ? 'YYYY-MM-DD' : 'MM-DD HH:mm'),
          dayjs(baseDate[1]).format(unit === 'day' ? 'YYYY-MM-DD' : 'MM-DD HH:mm'),
        ].join(' 至 ');
        const compareTimeRang = [
          dayjs(compareDate[0]).format(unit === 'day' ? 'YYYY-MM-DD' : 'MM-DD HH:mm'),
          dayjs(compareDate[1]).format(unit === 'day' ? 'YYYY-MM-DD' : 'MM-DD HH:mm'),
        ].join(' 至 ');

        const filterSeriesMap = filterLegendData.map(
          item => seriesMap.find(ti => ti.wdUniqKey === item.name) || { valSum: 0 },
        );
        const filterSeriesMapCompare = filterLegendData.map(
          item => seriesMapCompare.find(ti => ti.wdUniqKey === item.name) || { valSum: 0 },
        );

        option.title = [
          {
            subtext: thousandsBeautifyNumber(
              filterSeriesMap.reduce((base, item) => base + (item ? item.valSum : 0), 0),
            ),
            text: '数据项',
            subtextStyle: {
              color: '#222222',
              fontWeight: 'normal',
              fontStyle: 'normal',
              fontSize: 18,
            },
            textStyle: {
              color: '#666666',
              fontWeight: 'normal',
              fontStyle: 'normal',
              fontSize: 12,
            },
            left: this.showLegend ? '19.7%' : '24.5%',
            textAlign: 'center',
            textVerticalAlign: 'center',
            top: '45%',
          },
          {
            subtext: thousandsBeautifyNumber(
              filterSeriesMapCompare.reduce((base, item) => base + (item ? item.valSum : 0), 0),
            ),
            text: '数据项',
            subtextStyle: {
              color: '#222222',
              fontWeight: 'normal',
              fontStyle: 'normal',
              fontSize: 18,
            },
            textStyle: {
              color: '#666666',
              fontWeight: 'normal',
              fontStyle: 'normal',
              fontSize: 12,
            },
            left: this.showLegend ? '59.7%' : '74.5%',
            textAlign: 'center',
            textVerticalAlign: 'center',
            top: '45%',
          },
          {
            subtext: baseTimeRang,
            left: this.showLegend ? '20%' : '24.5%',
            top: '85%',
            textAlign: 'center',
            textVerticalAlign: 'center',
          },
          {
            subtext: compareTimeRang,
            left: this.showLegend ? '60%' : '74.5%',
            top: '85%',
            textAlign: 'center',
            textVerticalAlign: 'center',
          },
        ];

        option.series = [];

        option.series.push({
          type: 'pie',
          radius: ['50%', '60%'],
          avoidLabelOverlap: false,
          label: {
            formatter: '{d}%',
            ellipsis: true,
          },
          minAngle: 20,
          left: '0%',
          right: this.showLegend ? '60%' : '50%',
          data: filterSeriesMap.map(item => ({
            name: item.wdUniqKeyDesc,
            value: item.valSum,
          })),
        });
        option.series.push({
          type: 'pie',
          radius: ['50%', '60%'],
          avoidLabelOverlap: false,
          label: {
            formatter: '{d}%',
            ellipsis: true,
          },
          minAngle: 20,
          left: this.showLegend ? '40%' : '50%',
          right: this.showLegend ? '20%' : '0%',
          data: filterSeriesMapCompare.map(item => ({
            name: item.wdUniqKeyDesc,
            value: item.valSum,
          })),
        });
        option.legend = {
          type: 'scroll',
          orient: 'vertical',
          left: '85%',
          icon: 'circle',
          itemHeight: 10,
          itemWidth: 10,
          top: '20%',
          bottom: '20%',
          tooltip: {
            show: true,
          },
          formatter(name) {
            return name.length > 10 ? `${name.substr(0, 10)}...` : name;
          },
        };
      }

      option.tooltip = {
        trigger: 'item',
      };

      if (!this.showLegend) {
        delete option.legend;
      }

      return option;
    },
  },
  data() {
    return {
      //
    };
  },
};
</script>
